import { TFunction } from 'react-i18next';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Option } from '../../../../components/form/elements/select';
import {
  ObservableOptions,
  PagedOptions,
} from '../../../../components/form/elements/select/async-select';
import Classifier from '../../../../enums/classifier';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';
import { ProductObtainType } from '../../../../enums/product-obtain-type';
import ClassifierValue from '../../../../interfaces/classifierValue';
import Paginate from '../../../../interfaces/paginate';
import { DealService } from '../../../../services/deal.service';
import { ProductService } from '../../../../services/product.service';
import { isObject } from '../../../../services/utils';
import { categoryDataMap } from './async-categories';
import DealType from '../../../../enums/deal-type';

const OUTGOING_DEALS = [
  DealType.sold,
  DealType.self_consumed,
  DealType.recycled,
  DealType.spent,
  DealType.lost,
  DealType.moved,
];

export function typeDataMap(v: ClassifierValue): Option {
  return {
    label: v.value,
    value: v.id,
    veterinary: Object.values(v.properties).find(
      (v) =>
        v.property.key === ClassifierPropertyKey.VETERINARY_MEDICINAL_PRODUCT
    )?.value,
    units: Object.values(v.properties).find(
      (v) => v.property.key === ClassifierPropertyKey.UNITS_OF_MEASURE
    )?.value,
    parent: categoryDataMap(v.parent),
  };
}

export default function asyncTypes({
  t,
  dealService,
  productService,
  farmId,
  classifierKeys = [
    Classifier.PRODUCT_TYPES,
    Classifier.OBTAINED_PRODUCT_TYPES,
  ],
  dealType,
  handleSpecialProducts,
}: {
  t: TFunction;
  dealService: DealService;
  productService: ProductService;
  farmId: string;
  classifierKeys?: Classifier[];
  dealType: DealType;
  handleSpecialProducts: (value, key) => void;
}): ObservableOptions {
  return async (
    value,
    loadedOptions,
    { parentName, page, getValues, setValue }
  ): Promise<PagedOptions> => {
    let productObtainType = parentName
      ? getValues(parentName.concat('.product_obtain_type_id'))
      : getValues().product_obtain_type_id;

    if (isObject(productObtainType)) {
      productObtainType = (productObtainType as unknown as Option)
        ?.value as ProductObtainType;
    }

    if (
      !productObtainType ||
      [
        ProductObtainType.RECYCLE_GAINED,
        ProductObtainType.RECYCLE_LOST,
      ].includes(productObtainType)
    ) {
      productObtainType = null;
    } else if (classifierKeys.includes(Classifier.OBTAINED_PRODUCT_TYPES)) {
      productObtainType = [productObtainType, ProductObtainType.RECYCLE_GAINED];
    }

    const params: any = {
      page,
      classifier_key: classifierKeys,
      product_obtain_type_id: productObtainType,
      value,
    };

    const productCategoryId = getValues(
      parentName
        ? parentName.concat('.product_category_id')
        : 'product_category_id'
    )?.value;

    if (productCategoryId) {
      params['parent_id'] = productCategoryId;
    }

    const $types = dealService.classifierValues(farmId, params).pipe(
      map((response: Paginate<ClassifierValue>) => ({
        hasMore: response.current_page !== response.last_page,
        options: [
          {
            label: t('pages.warehouse.deals.product-types'),
            options: response.data.map(typeDataMap),
          },
        ],
        additional: { page: response.current_page + 1 },
      }))
    );

    const $usedProductTypes = productService
      .usedProductTypes(farmId, {
        classifier_key: classifierKeys,
        product_obtain_type_id: productObtainType,
        parent_id: productCategoryId,
      })
      .pipe(
        catchError(() => of([])),
        map((response) => ({
          label: t('pages.warehouse.deals.used-product-types'),
          options: response.map(typeDataMap),
        })),
        tap((response) => {
          if (
            OUTGOING_DEALS.includes(dealType) &&
            response.options.length === 1
          ) {
            setValue(`product_type_id`, response.options[0]);
            if (handleSpecialProducts) {
              handleSpecialProducts(response.options[0], 'type');
            }
          }
        })
      );

    const [types, used] = await Promise.all([
      $types.toPromise(),
      page === 1 && !value ? $usedProductTypes.toPromise() : null,
    ]);

    if (used) {
      types.options.unshift(used);
    }

    return types;
  };
}
